// deploy-pipelines-run-overrides (Wave 4, AC-27/OQ-C) — extends the pre-existing (Wave 2,
// AC-10/AC-11) trigger-error mapping, now extracted into its own pure helper so the copy is
// asserted directly by a jest suite rather than only through PipelineRunForm.
//
// `hadBranchOverride` distinguishes the two different 400 causes `TriggerRunAsync` can throw:
//   - "No build source found for image 'X'." — thrown unconditionally, before any override is
//     even looked at (PipelineRunService.cs ~line 101).
//   - A rejected PAT or branch (`RepoErrorConstants.InvalidPAT` / `BranchDoesnotExist`) — thrown
//     ONLY when an override branch was supplied, because `ValidateRepoAsync` is only called in
//     that case (PipelineRunService.cs:113-114).
// A caller that does not know whether it sent an override cannot tell these apart from the
// status code alone, so the caller passes it in.
//
// OQ-C, accepted cost: a rejected override branch costs the user their PAT — PipelineRunForm
// clears it on every submit path, success or failure. The server message already names the
// branch as the problem (it is generated with the branch name interpolated in); this helper
// appends the sentence stating the cost explicitly, per AC-27 — the cost is accepted, not
// hidden.
export const mapRunTriggerError = (error, hadBranchOverride) => {
	const status = error?.response?.status;
	const serverMessage = error?.response?.data?.message;

	if (status === 400 && hadBranchOverride) {
		const branchProblem = serverMessage || 'The branch you selected could not be validated.';
		return `${branchProblem} Enter your Personal Access Token again to retry.`;
	}
	if (status === 400) {
		return 'No build source found for this image — build the image first.';
	}
	// Epic-wide adversarial review (H1) + the independent review of its fix. There are now THREE
	// distinct 409 causes on this endpoint, and this branch used to answer all of them with the
	// "already in progress" copy:
	//   1. the pipeline already has an active run,
	//   2. a build of that image is already in progress,
	//   3. NEW — the run was cancelled while its build was being started (the cancel-vs-trigger
	//      race), where the BE now returns a message naming what happened to the abandoned build
	//      and whether retrying immediately will work.
	// For cause 3 the hard-coded copy was false in every clause the user could act on: the run they
	// just cancelled is terminal, nothing is in progress, and "wait for it to finish" is the
	// opposite of the correct advice. Preferring the server message fixes that without needing a
	// wire-format change, and mirrors what `mapCancelRunError` already does for its own 409. The
	// fallback is retained verbatim for causes 1 and 2, whose BE messages name an *image* rather
	// than something the user recognises.
	// Prefer the server's own message rather than sniffing it for a keyword: `PipelineRunService`
	// rejects exactly that pattern one layer down ("not a substring match on ex.Message, which
	// would silently break if the message ever changed"), and it would be no less brittle here.
	// All three BE 409 messages are already written for a human — an active run, an image build in
	// progress, or the cancelled-mid-trigger case — so passing them through is both simpler and
	// more accurate than any mapping this helper could infer. The generic copy stays as the
	// fallback for a 409 that arrives with no body at all.
	if (status === 409) {
		return (
			serverMessage ||
			'A run is already in progress for this pipeline (or its source image is currently being built) — please wait for it to finish.'
		);
	}
	if (status === 404) {
		return 'This pipeline no longer exists.';
	}
	if (status === 401 || status === 403) {
		return 'You do not have permission to run this pipeline.';
	}
	return error?.message || 'Something went wrong triggering the run.';
};

export default mapRunTriggerError;
